import { appState } from './state'
import { CardsState } from '../Models/CardsState'
import {
    stockCardX,
    stockCardY,
    tableauCardX,
    tableauCardY,
    foundationCardY,
    foundationCardX,
    wasteCardX,
    wasteCardY,
    isLeftHanded,
    tableauCardStep,
    isLandscapeLayout,
} from './allXYCoordinates'
import { getWasteVisibleCount } from './gameRules'

/**
 * Assigns x/y coordinates to every card in the given state based on its pile membership,
 * the current screen dimensions, and handedness setting. Returns a new CardsState.
 *
 * Accepts a CardsState (or partial) directly — no manual property spreading needed.
 */
export function assignCardCoordinates(state: Partial<CardsState>): CardsState {
    const stock = state.stock ?? []
    const tableau = state.tableau ?? ([[], [], [], [], [], [], []] as CardsState['tableau'])
    const waste = state.waste ?? []
    const foundation = state.foundation ?? ([[], [], [], []] as CardsState['foundation'])
    const draggedCards = state.draggedCards ?? []
    // 1. Assign coordinates to the cards in the stock.
    for (const card of stock) {
        const x = stockCardX()
        const y = stockCardY()

        card.x = x
        card.originalX = x
        card.y = y
        card.originalY = y
    }
    // 2. Assign coordinates to each card in each tableau
    for (let i = 0; i < tableau.length; i++) {
        let cumulativeOffset = 0

        for (const card of tableau[i]) {
            const baseX = tableauCardX(i)
            const baseY = tableauCardY(i)

            let x: number
            let y: number

            if (isLandscapeLayout()) {
                x = baseX
                y = baseY + cumulativeOffset
            } else {
                x = isLeftHanded() ? baseX + cumulativeOffset : baseX - cumulativeOffset
                y = baseY
            }

            card.x = x
            card.originalX = x
            card.y = y
            card.originalY = y

            cumulativeOffset += tableauCardStep(card.isFaceDown)
        }
    }

    // In portrait mode, compress overly long face-up cascades so bottom cards
    // remain within the viewport on smaller devices.
    if (!isLandscapeLayout()) {
        const minStep = Math.max(10, Math.floor((appState.cardWidth ?? 0) * 0.12))

        for (let i = 0; i < tableau.length; i++) {
            const pile = tableau[i]
            if (pile.length <= 1) continue

            const baseX = tableauCardX(i)
            const baseY = tableauCardY(i)
            const start = isLeftHanded() ? 1 : -1

            const steps = pile.slice(0, -1).map((c) => tableauCardStep(c.isFaceDown))
            const totalOffset = steps.reduce((sum, s) => sum + s, 0)
            // Determine available horizontal room so we can shrink only face-up steps.
            const maxRight = (appState.windowWidth ?? 0) - (appState.cardWidth ?? 0)
            const maxLeft = 0
            const available = isLeftHanded() ? maxRight - baseX : baseX - maxLeft
            if (available <= 0 || totalOffset <= available) continue

            const faceUpIndices: number[] = []
            for (let j = 0; j < pile.length - 1; j++) {
                if (!pile[j].isFaceDown) faceUpIndices.push(j)
            }
            if (faceUpIndices.length === 0) continue

            // Reduce face-up spacing just enough to fit within available width.
            const overflow = totalOffset - available
            const perStepReduction = Math.ceil(overflow / faceUpIndices.length)

            const adjustedSteps = [...steps]
            for (const idx of faceUpIndices) {
                adjustedSteps[idx] = Math.max(minStep, adjustedSteps[idx] - perStepReduction)
            }

            // Rebuild card X coordinates for this column with adjusted spacing.
            let adjustedOffset = 0
            for (let j = 0; j < pile.length; j++) {
                const card = pile[j]
                const x = baseX + start * adjustedOffset
                card.x = x
                card.originalX = x
                card.y = baseY
                card.originalY = baseY

                if (j < adjustedSteps.length) {
                    adjustedOffset += adjustedSteps[j]
                }
            }
        }
    }
    // 3. Assign Coordinates to each card in Foundation
    for (let i = 0; i < foundation.length; i++) {
        for (const card of foundation[i]) {
            const x = foundationCardX(i)
            const y = foundationCardY(i)

            card.x = x
            card.originalX = x
            card.y = y
            card.originalY = y
        }
    }
    // 4. Assign Coordinates to each card in Waste
    const maxVisible = getWasteVisibleCount(state as CardsState, appState.NUM_CARDS_FLIPPED)
    const firstVisibleIndex = Math.max(0, waste.length - maxVisible)
    for (let i = 0; i < waste.length; i++) {
        const card = waste[i]

        if (i >= firstVisibleIndex) {
            const visibleIndex = i - firstVisibleIndex
            const baseX = wasteCardX()
            const baseY = wasteCardY()

            // Offset each visible card
            const cardWidth = appState.cardWidth || 80 // fallback if undefined
            const cardOffset = visibleIndex * (cardWidth * 0.3) // Spacing (30%)

            // When drawing 3 in right-handed mode, stack from right to left
            // When drawing 1 or in left-handed mode, stack from left to right
            const isRightHandedDraw3 = !isLeftHanded() && appState.NUM_CARDS_FLIPPED === 3
            const offset = isRightHandedDraw3 ? -cardOffset : cardOffset

            card.x = baseX + offset
            card.originalX = baseX + offset
            card.y = baseY
            card.originalY = baseY
        } else {
            // Hidden waste cards stay in the same position
            const x = wasteCardX()
            const y = wasteCardY()

            card.x = x
            card.originalX = x
            card.y = y
            card.originalY = y
        }
    }

    return {
        stock,
        waste,
        foundation,
        tableau,
        draggedCards,
        wasteVisibleCount: state.wasteVisibleCount,
    }
}
